import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "../components/ui/select";
import { Trash2, AlertCircle, Info, Plus, Copy, Check } from "lucide-react";
import EmailCodePanel from "../components/EmailCodePanel";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { COIN_NETWORKS, COIN_OPTIONS, validateAddressFormat, isWalletComplete } from "../lib/walletValidation";
const PREFS = ["bonuses","rakeback","challenge_access","rewards_rebate"];
const WAGER_CATEGORIES = ["slots","house_originals","table_games","live_casino","sports","dice"];
const MAX_PREFERRED_SITES = 5;

const PROOF_ACCEPT = "image/jpeg,image/png,application/pdf,video/mp4,video/quicktime,video/webm";
const PROOF_CAPS = {
  "image/jpeg":      10 * 1024 * 1024,
  "image/png":       10 * 1024 * 1024,
  "application/pdf": 25 * 1024 * 1024,
  "video/mp4":       50 * 1024 * 1024,
  "video/quicktime": 50 * 1024 * 1024,
  "video/webm":      50 * 1024 * 1024,
};
const PROOF_EXTS = {
  "image/jpeg":       ["jpg", "jpeg"],
  "image/png":        ["png"],
  "application/pdf":  ["pdf"],
  "video/mp4":        ["mp4"],
  "video/quicktime":  ["mov"],
  "video/webm":       ["webm"],
};
const preCheckFile = (file) => {
  const ext = (file.name || "").split(".").pop().toLowerCase();
  const mime = (file.type || "").toLowerCase();
  const cap = PROOF_CAPS[mime];
  if (!cap) return { ok: false, code: "unsupported_type" };
  if (!(PROOF_EXTS[mime] || []).includes(ext)) return { ok: false, code: "extension_mismatch" };
  if (file.size > cap) return { ok: false, code: "file_too_large", maxMb: Math.floor(cap / (1024 * 1024)) };
  return { ok: true };
};
const fmt = (n) => (Number(n) || 0).toLocaleString();
const uid = () => Math.random().toString(36).slice(2, 10);

// Iter 189 — Support-request templates. Kept as plain string constants so
// the copy-to-clipboard action ships the exact bytes the player will send
// to the operator's support. English only — casino / case-site support
// teams handle these requests in English. Placeholders `[your username]`
// and `[your name]` stay as literal fill-ins per spec.
const CASINO_SUPPORT_TEMPLATE = `Subject: Request for my wagering activity statement (last 3 months)

Dear Support Team,

I would like to request a detailed statement of my wagering activity for my account (username: [your username]) covering the last 3 months.

Please provide my total wagered / turnover amount broken down by the following categories:
1. Slots
2. Live Casino
3. Table Games
4. Sports
5. House / Originals (in-house games, e.g. crash, plinko, mines)
6. Dice

If a full category breakdown is not available, please provide as much detail as possible, plus the total wagered amount for the period.

Kindly send this as a document, export, or screenshot that I can download. Thank you very much for your help.

Best regards,
[your name]`;

const CASE_SUPPORT_TEMPLATE = `Subject: Request for my wagering activity statement (last 3 months)

Dear Support Team,

I would like to request a detailed statement of my activity for my account (username: [your username]) covering the last 3 months.

Please provide my total wagered / turnover amount for the period — i.e. the total value of case / pack openings (or equivalent wagering) on my account over the last 3 months.

If a more detailed breakdown is available, please include it; otherwise the total wagered amount for the period is sufficient.

Kindly send this as a document, export, or screenshot that I can download. Thank you very much for your help.

Best regards,
[your name]`;

function SupportRequestReveal({ template, tid, t }) {
  // Iter 189 — Inline reveal panel with a copy-to-clipboard action.
  // Modal overlay avoided intentionally: the onboarding page is already
  // tall + form-heavy, and an inline reveal keeps the player's scroll
  // position anchored to the section they're filling in.
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(template);
      } else {
        // Fallback for non-secure contexts / older browsers.
        const ta = document.createElement("textarea");
        ta.value = template;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(t("onboarding.supportTpl.copied"));
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error(t("onboarding.supportTpl.copyFailed"));
    }
  };
  return (
    <div className="space-y-2" data-testid={tid}>
      <button
        type="button"
        data-testid={`${tid}-btn`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 border border-[#2DD4A7]/50 bg-[#2DD4A7]/[0.08] hover:bg-[#2DD4A7]/[0.16] text-[#2DD4A7] px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors"
      >
        <Copy className="w-3.5 h-3.5" />
        {t("onboarding.supportTpl.copyBtnLabel")}
      </button>
      {open && (
        <div className="border border-white/10 p-4 bg-[#0B0E14]" data-testid={`${tid}-reveal`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">{t("onboarding.supportTpl.langNote")}</div>
            <button
              type="button"
              data-testid={`${tid}-copy`}
              onClick={copy}
              className="inline-flex items-center gap-1.5 border border-[#2DD4A7]/50 bg-[#2DD4A7]/[0.08] hover:bg-[#2DD4A7]/[0.16] text-[#2DD4A7] px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? t("onboarding.supportTpl.copied") : t("onboarding.supportTpl.copy")}
            </button>
          </div>
          <pre
            className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-[#E8ECF4] bg-[#151B28] border border-white/8 p-4 overflow-x-auto"
            data-testid={`${tid}-body`}
          >{template}</pre>
        </div>
      )}
    </div>
  );
}


export default function Onboarding() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [casinos, setCasinos] = useState([]);
  const [caseOperators, setCaseOperators] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    // Iter 183 — Section 1 & 3 support multiple accounts, mirroring the
    // preferred-sites repeatable pattern from Section 6. Each entry carries
    // its own Gaming Account Name + operator selection (+ $ wager for case).
    // Section 2's category split stays ONE combined total across all casino
    // accounts. Case turnover sums across every caseAccounts entry.
    casinoAccounts: [{ _uid: uid(), gamingAccountName: "", casinoId: "", casinoOther: "" }],
    caseAccounts: [{ _uid: uid(), gamingAccountName: "", operatorId: "", operatorOther: "", wager: 0 }],
    streamerPreferences: [], streamerPrefNote: "",
    aboutMe: "",
    walletCoin: "USDT", walletNetwork: "ERC20", walletAddress: "",
    wagerCategoriesSelfReported: { slots: 0, house_originals: 0, table_games: 0, live_casino: 0, sports: 0, dice: 0 },
    // Iter 69 — offered volume per vertical (empty string until player types).
    // Persisted as {casino, case_opening} → offeredVolumeByVertical. Bounded
    // to 50%–200% of the vertical's weighted wager (see hints below).
    offeredCasino: "",
    offeredCase: "",
    // Iter 76 — TWO fully-independent lists, no shared state.
    preferredCasinos: [{ _uid: uid(), siteId: "", otherName: "" }],
    preferredCaseSites: [{ _uid: uid(), siteId: "", otherName: "" }],
    noPreferredCasino: false,
    noPreferredCaseSite: false,
    // Iter 193 — Reporting period + breakdown mode. FE sends RAW values
    // (never scaled) + period + mode; backend does 100% of the scaling.
    // Defaults keep pre-Iter-193 behaviour identical (categorized / 90 → scale 1.0).
    wagerReportMode: "categorized",         // "categorized" | "total_only"
    wagerReportPeriodDays: 90,              // 30 | 90
    wagerTotalSelfReported: 0,              // used only when mode === "total_only"
    // Iter 199 — Case has its OWN period + mode (parallel fields). Default
    // 90/categorized keeps existing behaviour byte-identical.
    caseReportMode: "categorized",
    caseReportPeriodDays: 90,
    // Iter 212 — "I don't play on a case site" opt-out. When true, all case
    // wager inputs are disabled and no case fields are persisted on submit,
    // so the player never appears on the Case marketplace. Casino unaffected.
    noCaseSite: false,
  });
  const [coeffCfg, setCoeffCfg] = useState(null);
  const [caseCfg, setCaseCfg] = useState(null);
  const [caseLaunchEnabled, setCaseLaunchEnabled] = useState(false);
  const [uploads, setUploads] = useState({});
  // Iter 192 — Reopen banner. When admin flags status=needs_more_info, the
  // player is routed back here (by ProtectedRoute). We surface the latest
  // moreInfoRequests entry at the top so they know exactly what to fix.
  const [reopenNote, setReopenNote] = useState(null);   // { message, sent_at, sent_by } | null

  useEffect(() => {
    api.get("/casinos").then((r) => {
      const rows = r.data || [];
      setCasinos(rows.filter((c) => (c.vertical || "casino") === "casino" && c.active !== false));
      setCaseOperators(rows.filter((c) => c.vertical === "case_opening" && c.active !== false));
    });
    api.get(`/players/${user.id}`).then((r) => {
      const pdoc = r.data || {};
      setProofs(pdoc.proofFiles || []);
      // Iter 192 — reopen banner (see below).
      if (pdoc.status === "needs_more_info") {
        const hist = Array.isArray(pdoc.moreInfoRequests) ? pdoc.moreInfoRequests : [];
        const latest = hist.length ? hist[hist.length - 1] : null;
        if (latest && latest.message) setReopenNote(latest);
      }
      // Iter 192 fix — hydrate the form from every previously-saved field
      // when the player has completed onboarding at least once (either
      // status="pending" awaiting admin review, or "needs_more_info"
      // reopened by admin, or "approved"). New (never-completed) onboarders
      // keep the default empty state so blank placeholders remain visible.
      // The iter-183 arrays (`casinoAccounts` / `caseAccounts`) are NOT
      // persisted server-side — they are re-derived here from the legacy
      // scalars the backend actually stores (currentCasino + gamingAccountName,
      // currentCaseOperator + caseGamingAccountName + caseWagerSelfReported).
      if (pdoc.onboardingComplete === true) {
        setF((old) => {
          const next = { ...old };
          // Section 1 — casino account (legacy single-entry → one iter-183 row)
          const cc = pdoc.currentCasino || {};
          const casinoRow = {
            _uid: uid(),
            gamingAccountName: pdoc.gamingAccountName || "",
            casinoId: cc.casinoId || "",
            casinoOther: cc.otherName || "",
          };
          next.casinoAccounts = [casinoRow];
          // Section 3 — case account (only when a case operator was saved)
          const co = pdoc.currentCaseOperator || {};
          const hasCase = !!(co.caseOperatorId || co.otherName || pdoc.caseGamingAccountName || pdoc.caseWagerSelfReported);
          if (hasCase) {
            next.caseAccounts = [{
              _uid: uid(),
              gamingAccountName: pdoc.caseGamingAccountName || "",
              operatorId: co.caseOperatorId || "",
              operatorOther: co.otherName || "",
              wager: Number(pdoc.caseWagerSelfReported || 0),
            }];
          }
          // Section 2 — wager categories
          if (pdoc.wagerCategoriesSelfReported && typeof pdoc.wagerCategoriesSelfReported === "object") {
            const src = pdoc.wagerCategoriesSelfReported;
            next.wagerCategoriesSelfReported = {
              slots:           Number(src.slots || 0),
              house_originals: Number(src.house_originals || 0),
              table_games:     Number(src.table_games || 0),
              live_casino:     Number(src.live_casino || 0),
              sports:          Number(src.sports || 0),
              dice:            Number(src.dice ?? src.other ?? 0),
            };
          }
          // Iter 193 — Hydrate the reporting mode/period/total. Legacy docs
          // without these fields land on defaults (categorized / 90). The
          // stored figures are already 90-day-equivalent (backend scaled at
          // submit); on reopen we render them as-is against a 90-day period
          // so the numbers round-trip unchanged.
          if (typeof pdoc.wagerReportMode === "string") next.wagerReportMode = pdoc.wagerReportMode;
          if (pdoc.wagerReportPeriodDays === 30 || pdoc.wagerReportPeriodDays === 90) {
            next.wagerReportPeriodDays = pdoc.wagerReportPeriodDays;
          }
          if (typeof pdoc.wagerTotalSelfReported === "number") {
            next.wagerTotalSelfReported = pdoc.wagerTotalSelfReported;
          }
          // Iter 199 — Hydrate the case-side parallel reporting fields. The
          // stored case turnover is already 90-day-equivalent (backend
          // scaled at submit), so we render it against a 90-day period on
          // reopen so the number round-trips unchanged.
          if (typeof pdoc.caseReportMode === "string") next.caseReportMode = pdoc.caseReportMode;
          if (pdoc.caseReportPeriodDays === 30 || pdoc.caseReportPeriodDays === 90) {
            next.caseReportPeriodDays = pdoc.caseReportPeriodDays;
          }
          // Offered volume per vertical
          const ov = pdoc.offeredVolumeByVertical || {};
          if (ov.casino) next.offeredCasino = String(ov.casino);
          if (ov.case_opening) next.offeredCase = String(ov.case_opening);
          // Section 5 — preferences / free-text
          if (Array.isArray(pdoc.streamerPreferences)) next.streamerPreferences = pdoc.streamerPreferences.slice();
          if (typeof pdoc.streamerPrefNote === "string") next.streamerPrefNote = pdoc.streamerPrefNote;
          if (typeof pdoc.aboutMe === "string") next.aboutMe = pdoc.aboutMe;
          // Section 6 — preferred sites, split back into two independent lists
          const ps = Array.isArray(pdoc.preferredSites) ? pdoc.preferredSites : [];
          const casinoPref = ps.filter((s) => (s.vertical || "casino") === "casino")
                                .map((s) => ({ _uid: uid(), siteId: s.siteId || "", otherName: s.otherName || "" }));
          const casePref = ps.filter((s) => s.vertical === "case_opening")
                              .map((s) => ({ _uid: uid(), siteId: s.siteId || "", otherName: s.otherName || "" }));
          if (casinoPref.length) next.preferredCasinos = casinoPref;
          if (casePref.length) next.preferredCaseSites = casePref;
          next.noPreferredCasino  = !!pdoc.noPreferredCasino;
          next.noPreferredCaseSite = !!pdoc.noPreferredCaseSite;
          // Section 7 — wallet (address stored on the doc as `cryptoWallet`)
          if (pdoc.walletCoin)    next.walletCoin    = pdoc.walletCoin;
          if (pdoc.walletNetwork) next.walletNetwork = pdoc.walletNetwork;
          if (pdoc.cryptoWallet)  next.walletAddress = pdoc.cryptoWallet;
          return next;
        });
      }
    });
    api.get("/config/wager-coefficients").then((r) => setCoeffCfg(r.data)).catch(() => {});
    api.get("/config/case-coefficients").then((r) => setCaseCfg(r.data)).catch(() => {});
    // Public case-launch flag — `/marketplace/meta` is streamer-only, so a
    // player onboarding can't read caseLaunchEnabled from there.
    api.get("/config/case-launch").then((r) => {
      setCaseLaunchEnabled(!!r.data?.launch_enabled);
    }).catch(() => {});
  }, [user.id]);

  const set = (k, v) => setF((old) => ({ ...old, [k]: v }));
  // Iter 194 — Period toggle is a PREDICATE LABEL, not a converter. Switching
  // between 30D and 90D means the player is now reporting a different real
  // dataset — force re-entry rather than transforming/carrying over stale
  // numbers. Category grid + total-only input are both cleared.
  const setReportPeriod = (d) => setF((old) => ({
    ...old,
    wagerReportPeriodDays: d,
    wagerCategoriesSelfReported: Object.fromEntries(WAGER_CATEGORIES.map((k) => [k, 0])),
    wagerTotalSelfReported: 0,
  }));
  // Iter 199 — Case period toggle is ALSO a predicate label (own parallel
  // field). Switching 30↔90 clears the case-wager inputs across all case
  // accounts. Does NOT touch the casino side.
  const setCaseReportPeriod = (d) => setF((old) => ({
    ...old,
    caseReportPeriodDays: d,
    caseAccounts: (old.caseAccounts || []).map((s) => ({ ...s, wager: 0 })),
  }));
  // Iter 114 — Changing coin OR network CLEARS the address so a leftover
  // e.g. Litecoin string can never be submitted after switching to USDC.
  const setCoin = (c) => setF((old) => ({ ...old, walletCoin: c, walletNetwork: COIN_NETWORKS[c][0], walletAddress: "" }));
  const setNetwork = (n) => setF((old) => ({ ...old, walletNetwork: n, walletAddress: "" }));
  const walletAddrError = validateAddressFormat(f.walletCoin, f.walletNetwork, f.walletAddress);
  const walletValid = isWalletComplete(f.walletCoin, f.walletNetwork, f.walletAddress);
  const togglePref = (p) => set("streamerPreferences", f.streamerPreferences.includes(p) ? f.streamerPreferences.filter((x) => x !== p) : [...f.streamerPreferences, p]);

  const upload = async (files) => {
    for (const file of files) {
      const pre = preCheckFile(file);
      if (!pre.ok) {
        toast.error(
          pre.code === "file_too_large"
            ? `${file.name}: too large (max ${pre.maxMb} MB for this type)`
            : pre.code === "extension_mismatch"
            ? `${file.name}: extension does not match the file content`
            : `${file.name}: unsupported file type`
        );
        continue;
      }
      const tempId = uid();
      setUploads((u) => ({ ...u, [tempId]: { name: file.name, pct: 0 } }));
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/players/me/proofs", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploads((u) => ({ ...u, [tempId]: { name: file.name, pct } }));
          },
        });
        setProofs((p) => [...p, { path: data.path, filename: data.filename, contentType: data.contentType, size: data.size }]);
        setUploads((u) => { const n = { ...u }; delete n[tempId]; return n; });
      } catch (e) {
        const d = e?.response?.data?.detail;
        const msg = (d && typeof d === "object" && d.message) ? d.message : apiErrorMessage(e);
        setUploads((u) => ({ ...u, [tempId]: { name: file.name, pct: 100, err: msg } }));
        toast.error(`${file.name}: ${msg}`);
      }
    }
  };
  const remove = async (path) => {
    try { await api.delete(`/players/me/proofs?path=${encodeURIComponent(path)}`); setProofs((p) => p.filter((x) => x.path !== path)); } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  // ---- Preferred sites: multi-entry list helpers ----
  const casinosPlusOther = [...casinos.map((c) => ({ id: c.id, name: c.name })), { id: "__other", name: t("onboarding.other") }];
  const caseOpsCase = caseOperators.filter((c) => (c.subVertical || "case_opening") === "case_opening");
  const caseOpsPack = caseOperators.filter((c) => c.subVertical === "pack_opening");

  // Iter 183 — multi-account: derive current keys from ALL entries so
  // preferred-site duplicate detection covers every listed casino/case account.
  const _keysOf = (rows, idKey, otherKey) => rows.map((a) => a[idKey] || (a[otherKey]?.trim() ? `other:${a[otherKey].trim().toLowerCase()}` : "")).filter(Boolean);
  const currentCasinoKeys = useMemo(() => _keysOf(f.casinoAccounts, "casinoId", "casinoOther"), [f.casinoAccounts]);
  const currentCaseKeys = useMemo(() => _keysOf(f.caseAccounts, "operatorId", "operatorOther"), [f.caseAccounts]);

  // Iter 76 — `caseVisible` must be resolved before we compute any per-list
  // validity below, otherwise `caseValid` throws a TDZ ReferenceError on
  // first render.
  const caseVisible = caseLaunchEnabled || user?.role === "admin";

  // Iter 76 — helpers per independent list. Each list has its own updater,
  // adder and remover — no cross-array reads or writes, so the two sections
  // cannot affect each other.
  const _makeListHelpers = (key) => ({
    update: (id, patch) => setF((old) => ({ ...old, [key]: old[key].map((s) => s._uid === id ? { ...s, ...patch } : s) })),
    add: () => setF((old) => {
      if (old[key].length >= MAX_PREFERRED_SITES) { toast.error(t("onboarding.preferredSites.max")); return old; }
      return { ...old, [key]: [...old[key], { _uid: uid(), siteId: "", otherName: "" }] };
    }),
    remove: (id) => setF((old) => ({ ...old, [key]: old[key].length === 1 ? old[key] : old[key].filter((s) => s._uid !== id) })),
  });
  const casinoList = _makeListHelpers("preferredCasinos");
  const caseList = _makeListHelpers("preferredCaseSites");

  const _hintsFor = (rows, currentKeys) => {
    const seen = new Map();
    const cur = new Set((currentKeys || []).filter(Boolean));
    return rows.map((s) => {
      const k = s.siteId || (s.otherName.trim() ? `other:${s.otherName.trim().toLowerCase()}` : "");
      const hint = {};
      if (!k) hint.needsSite = true;
      if (k && seen.has(k)) hint.duplicate = true;
      if (k && cur.has(k)) hint.matchesCurrent = true;
      if (k) seen.set(k, true);
      return hint;
    });
  };
  const casinoHints = useMemo(() => _hintsFor(f.preferredCasinos, currentCasinoKeys), [f.preferredCasinos, currentCasinoKeys]);
  const caseHints = useMemo(() => _hintsFor(f.preferredCaseSites, currentCaseKeys), [f.preferredCaseSites, currentCaseKeys]);

  // Iter 183 — repeatable-account helpers for Sections 1 & 3. Same shape as
  // the preferred-sites helpers above so behaviour reads identically.
  const casinoAcctList = {
    update: (id, patch) => setF((old) => ({ ...old, casinoAccounts: old.casinoAccounts.map((s) => s._uid === id ? { ...s, ...patch } : s) })),
    add: () => setF((old) => ({ ...old, casinoAccounts: [...old.casinoAccounts, { _uid: uid(), gamingAccountName: "", casinoId: "", casinoOther: "" }] })),
    remove: (id) => setF((old) => ({ ...old, casinoAccounts: old.casinoAccounts.length === 1 ? old.casinoAccounts : old.casinoAccounts.filter((s) => s._uid !== id) })),
  };
  const caseAcctList = {
    update: (id, patch) => setF((old) => ({ ...old, caseAccounts: old.caseAccounts.map((s) => s._uid === id ? { ...s, ...patch } : s) })),
    add: () => setF((old) => ({ ...old, caseAccounts: [...old.caseAccounts, { _uid: uid(), gamingAccountName: "", operatorId: "", operatorOther: "", wager: 0 }] })),
    remove: (id) => setF((old) => ({ ...old, caseAccounts: old.caseAccounts.length === 1 ? old.caseAccounts : old.caseAccounts.filter((s) => s._uid !== id) })),
  };
  const setCaseAcctWager = (id, raw) => {
    const cleaned = String(raw ?? "").replace(/\D+/g, "");
    const n = cleaned === "" ? 0 : parseInt(cleaned, 10);
    caseAcctList.update(id, { wager: n });
  };

  // Iter 76 — validity is independent per section. A "no preference" flag
  // completely bypasses that section's row checks.
  const casinoValid = f.noPreferredCasino || casinoHints.every((h) => !h.needsSite && !h.duplicate && !h.matchesCurrent);
  const caseValid = !caseVisible || f.noPreferredCaseSite || caseHints.every((h) => !h.needsSite && !h.duplicate && !h.matchesCurrent);
  const preferredSitesValid = casinoValid && caseValid;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      // Iter 76 — two independent lists → merge into `preferredSites` for BC
      // with the backend endpoint. Empty when the section has "no preference".
      const casinoRows = f.noPreferredCasino ? [] : f.preferredCasinos.map(({ _uid, ...r }) => ({ vertical: "casino", siteId: r.siteId || null, otherName: r.otherName || "" }));
      const caseRows = (!caseVisible || f.noPreferredCaseSite) ? [] : f.preferredCaseSites.map(({ _uid, ...r }) => ({ vertical: "case_opening", siteId: r.siteId || null, otherName: r.otherName || "" }));
      const preferredSites = [...casinoRows, ...caseRows];
      const firstCasino = preferredSites.find((p) => p.vertical === "casino");
      // Iter 69 — build offeredVolumeByVertical only for verticals where the
      // player entered a value AND passed the local bounds (server re-checks).
      const offeredVolumeByVertical = {};
      if (weightedWager > 0 && f.offeredCasino && !offeredCasinoError) {
        offeredVolumeByVertical.casino = Number(f.offeredCasino);
      }
      if (caseVisible && weightedCase > 0 && f.offeredCase && !offeredCaseError) {
        offeredVolumeByVertical.case_opening = Number(f.offeredCase);
      }
      // Iter 183 — surface the first entry of each repeatable list as the
      // legacy scalars the backend endpoint already knows about; the full
      // arrays go through as new opt-in fields (`casinoAccounts` /
      // `caseAccounts`) that the pydantic model can safely ignore since
      // no billing/verification touches them.
      const firstCasinoAcct = f.casinoAccounts[0] || {};
      const firstCaseAcct = f.caseAccounts[0] || {};
      const casinoAccountsPayload = f.casinoAccounts.map(({ _uid, ...a }) => a);
      const caseAccountsPayload = f.caseAccounts.map(({ _uid, ...a }) => a);
      await api.post("/players/me/onboarding", {
        ...f,
        // Iter 193 / 199 — RAW values only (no FE scaling — backend does 100% of it).
        // Casino: wagerReportMode + wagerReportPeriodDays (via `...f`).
        // Case: caseReportMode + caseReportPeriodDays (parallel fields, also via `...f`).
        wagerTotalSelfReported: f.wagerReportMode === "total_only" ? Number(f.wagerTotalSelfReported || 0) : 0,
        wagerReportRawEntered: {
          mode: f.wagerReportMode || "categorized",
          periodDays: Number(f.wagerReportPeriodDays || 90),
          categories: f.wagerReportMode === "total_only" ? {} : { ...(f.wagerCategoriesSelfReported || {}) },
          total: f.wagerReportMode === "total_only" ? Number(f.wagerTotalSelfReported || 0) : 0,
          caseWager: Number(caseWagerTotal || 0),
        },
        caseReportRawEntered: {
          mode: f.caseReportMode || "categorized",
          periodDays: Number(f.caseReportPeriodDays || 90),
          caseWager: Number(caseWagerTotal || 0),
        },
        gamingAccountName: firstCasinoAcct.gamingAccountName || "",
        currentCasinoId: firstCasinoAcct.casinoId || "",
        currentCasinoOther: firstCasinoAcct.casinoOther || "",
        caseGamingAccountName: firstCaseAcct.gamingAccountName || "",
        currentCaseOperatorId: firstCaseAcct.operatorId || "",
        currentCaseOperatorOther: firstCaseAcct.operatorOther || "",
        caseWagerSelfReported: caseWagerTotal,
        casinoAccounts: casinoAccountsPayload,
        caseAccounts: caseAccountsPayload,
        // Iter 212 — opt-out flag. Backend gates ALL case writes on this.
        noCaseSite: !!f.noCaseSite,
        currentWagerSelfReported: 0,
        preferredSites,
        offeredVolumeByVertical,
        // Legacy fields — kept only for graceful degradation if the backend
        // is older than this client. The new endpoint prefers preferredSites.
        preferredCasinoId: firstCasino?.siteId || "",
        preferredCasinoOther: firstCasino?.otherName || "",
        // Iter 75 — no per-row confirmation anymore; the BC field defaults to
        // True so downstream ingestion logic keeps working (no billing consumer).
        noExistingAccountAtPreferred: true,
        noPreferredCasino: !!f.noPreferredCasino,
        noPreferredCaseSite: !!f.noPreferredCaseSite,
      });
      toast.success(t("onboarding.submitted"));
      await refresh(); nav("/player");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "free_text.blocked" && d.category) {
        const key = `freeText.blocked.${d.category}`;
        const msg = t(key);
        toast.error(`${d.field === "aboutMe" ? t("onboarding.aboutMe") : t("onboarding.prefNote")}: ${msg && msg !== key ? msg : (d.message || t("freeText.blocked.generic"))}`);
      } else if (d && typeof d === "object" && d.message) {
        toast.error(d.message);
      } else {
        toast.error(apiErrorMessage(err, "Submission failed"));
      }
    } finally { setBusy(false); }
  };

  const wagerAmounts = f.wagerCategoriesSelfReported;
  const wagerCategoriesSum = WAGER_CATEGORIES.reduce((a, k) => a + Number(wagerAmounts?.[k] || 0), 0);
  const coefMap = (coeffCfg?.categories || []).reduce((m, c) => { m[c.key] = c.coefficient; return m; }, {});
  // Iter 221 — Weighted Wager displayed in the preview = raw × (90/period).
  // Raw entered figures are stored verbatim (Iter 194 preserved); the
  // ×(90/period) projection is applied ONLY when deriving the "Weighted
  // Wager" headline and the 50/200% Offered Volume bounds, so a 30-day
  // reporter and an equivalent 90-day reporter see the same commitment
  // basis. Mirrors backend `_project_weighted` (server.py) — the two
  // formulas MUST stay identical; the shared JS helper is `projectWeighted`
  // in /app/frontend/src/lib/wagerWeighted.js.
  const isTotalOnly = f.wagerReportMode === "total_only";
  const _lumpCasino = Number(coeffCfg?.lump?.casino ?? 0.45);
  const _lumpCase   = Number(coeffCfg?.lump?.case_opening ?? 1.0);
  const _wagerScale = 90 / Number(f.wagerReportPeriodDays || 90);   // 30→3.0, 90→1.0
  const _caseScale  = 90 / Number(f.caseReportPeriodDays  || 90);
  const _rawWeightedCasino = isTotalOnly
    ? Number(f.wagerTotalSelfReported || 0) * _lumpCasino
    : WAGER_CATEGORIES.reduce((a, k) => a + Number(wagerAmounts?.[k] || 0) * Number(coefMap[k] || 0), 0);
  const weightedWager = Math.round(_rawWeightedCasino * _wagerScale * 100) / 100;
  const setCategory = (k, raw) => {
    const cleaned = String(raw ?? "").replace(/\D+/g, "");
    const n = cleaned === "" ? 0 : parseInt(cleaned, 10);
    setF((old) => ({
      ...old,
      wagerCategoriesSelfReported: { ...old.wagerCategoriesSelfReported, [k]: n },
    }));
  };

  const caseWagerTotal = f.noCaseSite ? 0 : f.caseAccounts.reduce((a, x) => a + Number(x.wager || 0), 0);
  const caseWager = caseWagerTotal;
  // Iter 33: with a single Wager field, the weighted case turnover equals
  // the entered wager (coefficient 1.0). Iter 183: entered wager is now
  // the SUM across every case-site entry.
  // Iter 221 — Case follows the same rule as Casino: projection by
  // (90/caseReportPeriodDays) applied at DERIVE (display + bounds) only.
  // Raw entered case wager stays stored verbatim.
  const weightedCase = Math.round((caseWager * _lumpCase * _caseScale) * 100) / 100;

  // Iter 69 — offered volume: gated behind a non-zero weighted wager for
  // that vertical and bounded to 50%–200% of it. Client-side validation
  // gives instant feedback; server is authoritative (mirrors the same
  // range with 422 offered_volume_{below_min,above_max}).
  const setOffered = (k, raw) => {
    const cleaned = String(raw ?? "").replace(/[^\d]/g, "");
    setF((old) => ({ ...old, [k]: cleaned }));
  };
  const offeredBoundsFor = (weighted) => {
    const w = Math.max(0, Math.round(Number(weighted) || 0));
    return { min: Math.round(w * 0.5), max: Math.round(w * 2), weighted: w };
  };
  const offeredCasinoNum = Number(f.offeredCasino || 0);
  const offeredCaseNum = Number(f.offeredCase || 0);
  const casinoBounds = offeredBoundsFor(weightedWager);
  const caseBounds = offeredBoundsFor(weightedCase);
  const casinoOfferedGated = !(weightedWager > 0);
  const caseOfferedGated = !(weightedCase > 0);
  const offeredCasinoError = casinoOfferedGated || !f.offeredCasino
    ? null
    : offeredCasinoNum < casinoBounds.min
      ? t("onboarding.offered.belowMin").replace("{min}", fmt(casinoBounds.min))
      : offeredCasinoNum > casinoBounds.max
        ? t("onboarding.offered.aboveMax").replace("{max}", fmt(casinoBounds.max))
        : null;
  const offeredCaseError = caseOfferedGated || !f.offeredCase
    ? null
    : offeredCaseNum < caseBounds.min
      ? t("onboarding.offered.belowMin").replace("{min}", fmt(caseBounds.min))
      : offeredCaseNum > caseBounds.max
        ? t("onboarding.offered.aboveMax").replace("{max}", fmt(caseBounds.max))
        : null;
  const offeredValid = !offeredCasinoError && !offeredCaseError;

  // Dynamic step numbering — inject Case as its own numbered step ONLY when
  // caseVisible is true. Everything after shifts up by 1. Otherwise numbering
  // stays 1-7 exactly as before. `caseVisible` itself is declared earlier
  // (top of the render body) to avoid TDZ errors.
  const stepNums = useMemo(() => {
    // iter 36: Steps 1 (account name) + 2 (casino selection) are now a single
    // combined block. `stepNums.name` and `stepNums.currentCasino` therefore
    // point at the SAME number (Step 1) so the label/testid contracts stay
    // stable while the visible section count drops by one.
    let n = 1;
    const combined = n++;
    const nums = { name: combined, currentCasino: combined, wager: n++ };
    if (caseVisible) { nums.currentCase = n++; nums.case = n++; }
    nums.proofs = n++;
    nums.preferences = n++;
    nums.preferredSites = n++;
    nums.wallet = n++;
    return nums;
  }, [caseVisible]);

  if (!user) return <div className="p-10 text-[#8B97AE]" data-testid="onb-need-login">Please log in.</div>;
  if (user.emailVerified === false) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24" data-testid="onboarding-email-locked">
        <EmailCodePanel testIdPrefix="onb" />
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
      {/* Iter 181 — Step-2 eyebrow removed per copy cleanup. The i18n key
          `onboarding.step` is left in place for potential reuse elsewhere. */}
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3" data-testid="onboarding-title">{t("onboarding.title")}</h1>
      <p className="text-sm text-[#8B97AE] mb-10">{t("onboarding.intro")}</p>

      {/* Iter 192 — Reopen banner. Rendered above the form when admin has
          asked for more information. Nothing on the player doc is wiped —
          the fields below are pre-filled with whatever they last saved. */}
      {reopenNote && (
        <div
          className="border border-[#F5B841]/60 bg-[#F5B841]/[0.08] p-4 lg:p-5 mb-8"
          data-testid="onboarding-reopen-banner"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-[#F5B841]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5B841]">
              The VG team needs more information
            </span>
          </div>
          <div
            className="font-mono text-[13px] leading-relaxed text-[#E8ECF4] whitespace-pre-wrap"
            data-testid="onboarding-reopen-message"
          >{reopenNote.message}</div>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="onboarding-reopen-meta">
            Sent {(() => { try { return new Date(reopenNote.sent_at).toLocaleString(); } catch { return reopenNote.sent_at; } })()}
            {reopenNote.sent_by ? <> · by {String(reopenNote.sent_by).includes("@") ? "Admin" : reopenNote.sent_by}</> : null}
          </div>
          <div className="mt-3 text-[11px] font-mono text-[#8B97AE]">
            Your previous answers are still here — edit or add what's needed below, then submit again.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-8" data-testid="onboarding-form">
        {/* Iter 36: former Steps 1 (account name) + 2 (casino selection) are
            merged into a single "Your current casino" block, mirroring the
            case block's shape (name field on top, operator dropdown below). */}
        <section className="border border-white/8 p-6 space-y-4" data-testid="onb-current-casino">
          <h2 className="font-display font-bold text-lg">{stepNums.currentCasino} · {t("onboarding.currentCasino")}</h2>
          {/* Iter 181 — "Reference only. Never used in billing." subtitle
              removed; the same disclaimer still appears on the Weighted-wager
              tooltip so the rule remains visible without cluttering the header.
              Iter 183 — repeatable-account list. Each entry has its own
              Gaming Account Name + Casino selector, mirroring the Preferred
              Sites (Section 6) pattern. */}
          {f.casinoAccounts.map((acct, i) => (
            <div
              key={acct._uid}
              data-testid={`ob-casino-acct-${i}`}
              className={`space-y-3 ${i > 0 ? "pt-4 border-t border-white/10" : ""}`}
            >
              {f.casinoAccounts.length > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                    {t("onboarding.accountNumber").replace("{n}", String(i + 1))}
                  </div>
                  <button
                    type="button"
                    data-testid={`ob-casino-acct-remove-${i}`}
                    onClick={() => casinoAcctList.remove(acct._uid)}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] hover:text-[#F26D6D]/80 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />{t("onboarding.removeAccount")}
                  </button>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("onboarding.gamingAccountName")}</Label>
                <Input
                  data-testid={i === 0 ? "ob-gamingAccountName" : `ob-gamingAccountName-${i}`}
                  required
                  minLength={2}
                  value={acct.gamingAccountName}
                  onChange={(e) => casinoAcctList.update(acct._uid, { gamingAccountName: e.target.value })}
                  className="rounded-none bg-transparent border-white/20 h-11 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("onboarding.currentCasino")}</Label>
                <Select
                  value={acct.casinoId || (acct.casinoOther ? "__other" : "")}
                  onValueChange={(v) => casinoAcctList.update(acct._uid, { casinoId: v === "__other" ? "" : v, casinoOther: v === "__other" ? acct.casinoOther : "" })}
                >
                  <SelectTrigger data-testid={i === 0 ? "ob-currentCasino" : `ob-currentCasino-${i}`} className="rounded-none bg-transparent border-white/20 h-11 mt-1"><SelectValue placeholder={t("onboarding.chooseCasino")} /></SelectTrigger>
                  <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">{casinosPlusOther.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                {(acct.casinoId === "" || acct.casinoOther) && (
                  <Input
                    data-testid={i === 0 ? "ob-currentCasinoOther" : `ob-currentCasinoOther-${i}`}
                    placeholder={t("onboarding.otherName")}
                    value={acct.casinoOther}
                    onChange={(e) => casinoAcctList.update(acct._uid, { casinoOther: e.target.value })}
                    className="rounded-none bg-transparent border-white/20 h-11 mt-2"
                  />
                )}
              </div>
            </div>
          ))}
          <div>
            <button
              type="button"
              data-testid="ob-casino-acct-add"
              onClick={casinoAcctList.add}
              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-[#2DD4A7]/80 transition-colors"
            >
              <Plus className="w-3 h-3" />{t("onboarding.addCasinoAccount")}
            </button>
          </div>
        </section>

        <section className="border border-white/8 p-6 space-y-4">
          {/* Iter 181 — heading renamed "Casino Wager"; the long orange
              disclaimer + "Rough split" line are dropped. A short mint
              caption underneath restates the rule succinctly. */}
          <h2 className="font-display font-bold text-lg">{stepNums.wager} · {t("onboarding.casinoWagerTitle")}</h2>
          {/* Iter 183 — green caption removed (confirming Iter-181 request);
              the same disclaimer stays one tap away in the weighted tooltip.
              Added a subtle hint that the split is COMBINED across all
              casino accounts listed in Section 1. */}
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-categories-combined-hint">
            {t("onboarding.categoriesCombinedHint")}
          </p>
          {/* Iter 189 — copyable support-request template for the casino
              vertical. Sits below the subtitle, mirrors the styling used
              in the How-It-Works guide's template block. */}
          <SupportRequestReveal template={CASINO_SUPPORT_TEMPLATE} tid="ob-casino-support-tpl" t={t} />
          {/* Iter 193 — Reporting period + breakdown-mode toggles. Defaults
              (categorized / 90) keep the form byte-identical to today. Only
              the wager INPUTS scale by 90/period; offered volume is always
              90-day and never scaled here — see the helper caption below. */}
          <div className="flex flex-wrap gap-4 pt-1" data-testid="ob-report-toggles">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("onboarding.reportPeriodLabel")}</div>
              <div className="inline-flex border border-white/20" role="tablist">
                {[30, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`ob-report-period-${d}`}
                    disabled={!!f.offeredCasino}
                    onClick={() => setReportPeriod(d)}
                    className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      Number(f.wagerReportPeriodDays) === d
                        ? "bg-[#2DD4A7] text-[#04150F]"
                        : "text-[#8B97AE] hover:text-[#E8ECF4]"
                    }`}
                  >{d === 30 ? t("card.wager.period30") : t("card.wager.period90")}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("onboarding.reportModeLabel")}</div>
              <div className="inline-flex border border-white/20" role="tablist">
                {[
                  { v: "categorized", label: t("onboarding.reportMode.categorized") },
                  { v: "total_only",  label: t("onboarding.reportMode.totalOnly") },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    data-testid={`ob-report-mode-${m.v}`}
                    disabled={!!f.offeredCasino}
                    onClick={() => set("wagerReportMode", m.v)}
                    className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      f.wagerReportMode === m.v
                        ? "bg-[#2DD4A7] text-[#04150F]"
                        : "text-[#8B97AE] hover:text-[#E8ECF4]"
                    }`}
                  >{m.label}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-2" data-testid="ob-wager-categories">
            {isTotalOnly ? (
              <div data-testid="ob-total-only-input-wrap">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("onboarding.totalOnly.label")}</Label>
                <div className="mt-1 flex items-center gap-2 max-w-sm">
                  <span className="text-[#8B97AE] font-mono text-sm px-1">$</span>
                  <Input
                    data-testid="ob-total-only-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={!!f.offeredCasino}
                    value={f.wagerTotalSelfReported ? fmt(f.wagerTotalSelfReported) : ""}
                    onChange={(e) => {
                      const digits = String(e.target.value || "").replace(/[^0-9]/g, "");
                      set("wagerTotalSelfReported", digits === "" ? 0 : parseInt(digits, 10));
                    }}
                    placeholder="0"
                    className="rounded-none bg-transparent border-white/20 h-10 font-mono tabular-nums text-right disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                  />
                </div>
                <p className="mt-2 text-[10px] font-mono text-[#8B97AE]" data-testid="ob-total-only-hint">
                  {t("onboarding.totalOnly.hint")}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ob-wager-categories-grid">
              {WAGER_CATEGORIES.map((k) => (
                <div key={k}>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t(`categories.${k}`)}</Label>
                  <Input
                    data-testid={`ob-category-${k}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={!!f.offeredCasino}
                    value={fmt(wagerAmounts[k])}
                    onChange={(e) => setCategory(k, e.target.value)}
                    className="rounded-none bg-transparent border-white/20 h-9 font-mono tabular-nums text-right disabled:opacity-40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                  />
                </div>
              ))}
            </div>
            )}
            {f.offeredCasino && (
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-cat-locked-casino">{t("onboarding.offered.categoriesLocked")}</div>
            )}
            {!isTotalOnly && (
            <div className="mt-4 flex flex-wrap items-baseline gap-2 text-[10px] font-mono uppercase tracking-widest" data-testid="ob-category-sum">
              <span className="text-[#8B97AE]">{t("onboarding.categories.totalLabel")}:</span>
              <span
                className="tabular-nums text-[#E8ECF4]"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                data-testid="ob-total-wager"
              >${fmt(wagerCategoriesSum)}</span>
            </div>
            )}
            <div className="mt-3 border border-[#2DD4A7]/30 bg-[#2DD4A7]/5 p-3" data-testid="ob-weighted-wager">
              <div className="flex items-center gap-2">
                {/* Iter 181 — "(estimate)" dropped from the label; the info button
                    is now larger + higher-contrast so it reads as clearly clickable. */}
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">{t("onboarding.weightedLabelPlain")}</div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="ob-weighted-info-btn"
                      aria-label={t("onboarding.categories.weightedHowLabel")}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-[#2DD4A7] bg-[#2DD4A7] text-[#04150F] hover:bg-[#2DD4A7]/85 hover:text-[#04150F] focus:outline-none focus:ring-2 focus:ring-[#2DD4A7]/70 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    data-testid="ob-weighted-info-popover"
                    className="w-80 rounded-none border-[#2DD4A7]/40 bg-[#0B0E14] text-[#E8ECF4] p-4 shadow-xl"
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#2DD4A7]">{t("onboarding.categories.weightedHowLabel")}</div>
                      {coeffCfg?.version ? (
                        <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B97AE]">v{coeffCfg.version}</div>
                      ) : null}
                    </div>
                    <p className="mt-2 text-xs text-[#E8ECF4] leading-relaxed">{t("onboarding.categories.weightedFormula")}</p>
                    <ul className="mt-3 space-y-1 border-t border-white/10 pt-3">
                      {(coeffCfg?.categories || []).map((c) => {
                        const pct = Math.round((Number(c.coefficient) || 0) * 100);
                        const amount = Number(wagerAmounts?.[c.key] || 0);
                        const contribution = amount * (Number(c.coefficient) || 0);
                        return (
                          <li
                            key={c.key}
                            data-testid={`ob-weighted-coef-${c.key}`}
                            className="flex items-baseline justify-between gap-3 text-[11px]"
                          >
                            <span className="text-[#E8ECF4]">{t(`categories.${c.key}`)}</span>
                            <span
                              className="tabular-nums text-[#8B97AE]"
                              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                            >
                              {fmt(amount)} × {pct}% = <span className={contribution === 0 ? "text-[#8B97AE]" : "text-[#E8ECF4]"}>{fmt(Math.round(contribution))}</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="mt-3 border border-[#F5B841]/40 bg-[#F5B841]/5 px-3 py-2 text-[11px] text-[#F5B841] leading-relaxed" data-testid="ob-weighted-dice-note">
                      {t("onboarding.categories.weightedDiceNote")}
                    </div>
                    {/* Iter 181 — surface the removed inline disclaimer here so it
                        stays one tap away without cluttering the calculator card. */}
                    <p className="mt-3 text-xs text-[#8B97AE] leading-relaxed" data-testid="ob-weighted-tooltip-disclaimer">
                      {t("onboarding.weightedTooltipBrief")}
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Iter 181 — "SELF-REPORTED ESTIMATE · … · V1" caption removed;
                  version is still exposed in the tooltip's top-right corner. */}
              <div className="mt-1">
                <span
                  className="font-display text-3xl font-black tracking-tighter tabular-nums text-[#E8ECF4]"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                  data-testid="ob-weighted-value"
                >${fmt(Math.round(weightedWager))}</span>
              </div>
            </div>
            {/* Iter 69 — offered volume (casino). Gated until weightedWager > 0.
                Bounds displayed inline; server re-validates. Capture-only
                this iteration — pricing basis unchanged. */}
            <div className="mt-3 border border-[#F5B841]/30 bg-[#F5B841]/[0.04] p-3" data-testid="ob-offered-casino-card">
              <div className="flex items-center gap-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" htmlFor="ob-offered-casino">{t("onboarding.offered.label")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="ob-offered-casino-info-btn"
                      aria-label={t("onboarding.offered.infoTitle")}
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#F5B841]/60 text-[#F5B841] hover:bg-[#F5B841]/10 focus:outline-none focus:ring-2 focus:ring-[#F5B841]/60 transition-colors"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    data-testid="ob-offered-casino-info-popover"
                    className="w-80 rounded-none border-[#F5B841]/40 bg-[#0B0E14] text-[#E8ECF4] p-4 shadow-xl"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5B841] mb-2">{t("onboarding.offered.infoTitle")}</div>
                    <p className="text-xs text-[#E8ECF4] leading-relaxed">{t("onboarding.offered.infoBody")}</p>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Iter 181 — gold accent caption clarifying the offered volume is
                  a proposal, not a binding commitment. */}
              <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-offered-casino-not-binding">
                {t("onboarding.offeredNotBinding")}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[#8B97AE] font-mono text-sm px-1">$</span>
                <Input
                  id="ob-offered-casino"
                  data-testid="ob-offered-casino"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={casinoOfferedGated}
                  value={f.offeredCasino ? fmt(offeredCasinoNum) : ""}
                  onChange={(e) => setOffered("offeredCasino", e.target.value)}
                  placeholder={casinoOfferedGated ? "" : t("onboarding.offered.placeholder").replace("{min}", fmt(casinoBounds.min)).replace("{max}", fmt(casinoBounds.max))}
                  className="rounded-none bg-transparent border-white/20 h-10 font-mono tabular-nums text-right disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                />
              </div>
              {/* Iter 181 — gated hint copy removed; input stays visually
                  disabled via `disabled` prop until a wager is entered. */}
              {!casinoOfferedGated && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-offered-casino-range">
                  {t("onboarding.offered.range").replace("{min}", fmt(casinoBounds.min)).replace("{max}", fmt(casinoBounds.max))}
                </div>
              )}
              {/* Iter 221 — Casino projected-90d caption restored. Now
                  accurate again: the range displayed IS the 90-day-
                  equivalent commitment basis, projected from the raw
                  entered figure. */}
              {Number(f.wagerReportPeriodDays) === 30 && !casinoOfferedGated && (
                <div className="mt-2 text-[10px] font-mono text-[#F5B841]" data-testid="ob-offered-casino-90d-hint">
                  {t("onboarding.offered.projected90d")}
                </div>
              )}
              {offeredCasinoError && (
                <div className="mt-2 text-[11px] font-mono text-[#F26D6D]" data-testid="ob-offered-casino-error">{offeredCasinoError}</div>
              )}
            </div>
          </div>
        </section>

        {caseVisible && (
          <section className="border border-white/8 p-6 space-y-4" data-testid="onb-current-case-section">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display font-bold text-lg">{stepNums.currentCase} · {t("onboarding.currentCase")}</h2>
              {!caseLaunchEnabled && <span className="text-[9px] font-mono uppercase tracking-widest text-[#F5B841]">admin preview</span>}
            </div>
            {/* Iter 212 — Repeatable case-account rows (name + operator only).
                Mirrors Section 1's "Your current casino" pattern. The wager
                input stays in Section 2 (Case-Site Wager) so a player can
                declare their case sites even if they use the opt-out toggle
                there. All account state lives on `f.caseAccounts` — the
                Section 2 wager map iterates the SAME array so add/remove
                here flows through to the wager rows automatically. */}
            {f.caseAccounts.map((acct, i) => (
              <div
                key={acct._uid}
                data-testid={`ob-current-case-acct-${i}`}
                className={`space-y-3 ${i > 0 ? "pt-4 border-t border-white/10" : ""}`}
              >
                {f.caseAccounts.length > 1 && (
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                      {t("onboarding.accountNumber").replace("{n}", String(i + 1))}
                    </div>
                    <button
                      type="button"
                      data-testid={`ob-current-case-acct-remove-${i}`}
                      onClick={() => caseAcctList.remove(acct._uid)}
                      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] hover:text-[#F26D6D]/80 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />{t("onboarding.removeAccount")}
                    </button>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("onboarding.case.gamingAccountName")}</Label>
                  <Input
                    data-testid={i === 0 ? "ob-current-case-name" : `ob-current-case-name-${i}`}
                    minLength={2}
                    value={acct.gamingAccountName}
                    onChange={(e) => caseAcctList.update(acct._uid, { gamingAccountName: e.target.value })}
                    className="rounded-none bg-transparent border-white/20 h-11 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("onboarding.case.operator")}</Label>
                  <Select
                    value={acct.operatorId || (acct.operatorOther ? "__other" : "")}
                    onValueChange={(v) => caseAcctList.update(acct._uid, { operatorId: v === "__other" ? "" : v, operatorOther: v === "__other" ? acct.operatorOther : "" })}
                  >
                    <SelectTrigger data-testid={i === 0 ? "ob-current-case-operator" : `ob-current-case-operator-${i}`} className="rounded-none bg-transparent border-white/20 h-11 mt-1"><SelectValue placeholder={t("onboarding.preferredSites.pickSite")} /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
                      {caseOpsCase.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("onboarding.case.groupCase")}</SelectLabel>
                          {caseOpsCase.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      {caseOpsPack.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("onboarding.case.groupPack")}</SelectLabel>
                          {caseOpsPack.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      <SelectItem value="__other">{t("onboarding.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {!acct.operatorId && (
                    <Input
                      data-testid={i === 0 ? "ob-current-case-operator-other" : `ob-current-case-operator-other-${i}`}
                      className="mt-2 rounded-none bg-transparent border-white/20 h-10"
                      placeholder={t("onboarding.case.operatorOther")}
                      value={acct.operatorOther}
                      onChange={(e) => caseAcctList.update(acct._uid, { operatorOther: e.target.value })}
                    />
                  )}
                </div>
              </div>
            ))}
            <div>
              <button
                type="button"
                data-testid="ob-current-case-acct-add"
                onClick={caseAcctList.add}
                className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-[#2DD4A7]/80 transition-colors"
              >
                <Plus className="w-3 h-3" />{t("onboarding.addCaseAccount")}
              </button>
            </div>
            {/* Iter 217 — Opt-out control moved OUT of Section 4 and into
                Section 3 below the add-account button so it's the most
                prominent decision after listing case sites. Styled as a
                large amber-bordered switch row (not a plain checkbox) so
                it clearly signals "toggle" rather than "form field". When
                on, Section 4 is hidden entirely (see gate below). */}
            <label
              className={`flex items-center justify-between gap-4 border p-4 cursor-pointer transition-colors ${
                f.noCaseSite
                  ? "border-[#F5B841] bg-[#F5B841]/[0.08]"
                  : "border-white/20 hover:border-[#F5B841]/60"
              }`}
              data-testid="ob-no-case-site-label"
            >
              <div className="flex-1">
                <div className={`text-sm font-semibold ${f.noCaseSite ? "text-[#F5B841]" : "text-[#E8ECF4]"}`}>
                  {t("onboarding.case.noSiteLabel")}
                </div>
                <div className="text-[11px] text-[#8B97AE] mt-1">
                  {t("onboarding.case.noSiteHint")}
                </div>
              </div>
              <div className="relative shrink-0">
                <input
                  type="checkbox"
                  data-testid="ob-no-case-site"
                  checked={!!f.noCaseSite}
                  onChange={(e) => set("noCaseSite", e.target.checked)}
                  className="peer sr-only"
                />
                <span className="block w-12 h-6 bg-white/15 peer-checked:bg-[#F5B841] transition-colors" aria-hidden="true"></span>
                <span className="block w-5 h-5 bg-[#E8ECF4] absolute top-0.5 left-0.5 peer-checked:translate-x-6 transition-transform" aria-hidden="true"></span>
              </div>
            </label>
          </section>
        )}

        {caseVisible && !f.noCaseSite && (
          <section className="border border-white/8 p-6 space-y-4" data-testid="onb-case-section">
            <div className="flex items-baseline justify-between">
              {/* Iter 181 — case section heading renamed "Case-Site Wager";
                  long orange disclaimer removed. Mint caption below matches
                  the casino block for parallelism. */}
              <h2 className="font-display font-bold text-lg">{stepNums.case} · {t("onboarding.caseWagerTitle")}</h2>
              {!caseLaunchEnabled && <span className="text-[9px] font-mono uppercase tracking-widest text-[#F5B841]">admin preview</span>}
            </div>
            {/* Iter 187 — subtitle mirrors Section 2's Combined-hint typography. */}
            <p className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-case-wager-subtitle">
              {t("onboarding.caseWagerSubtitle")}
            </p>
            {/* Iter 189 — case variant of the support-request template. */}
            <SupportRequestReveal template={CASE_SUPPORT_TEMPLATE} tid="ob-case-support-tpl" t={t} />
            {/* Iter 199 — Case-vertical period + mode toggles. Own parallel
                fields (caseReportPeriodDays / caseReportMode); do NOT reuse
                the casino wagerReport* keys. Mode is a transparency marker
                only (case coefficient + lump are both 1.0). Lock once the
                player has typed an offered-case value, mirroring the
                casino-side lock. */}
            <div className="flex flex-wrap gap-4 pt-1" data-testid="ob-case-report-toggles">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("onboarding.reportPeriodLabel")}</div>
                <div className="inline-flex border border-white/20" role="tablist">
                  {[30, 90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      data-testid={`ob-case-report-period-${d}`}
                      disabled={!!f.offeredCase}
                      onClick={() => setCaseReportPeriod(d)}
                      className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        Number(f.caseReportPeriodDays) === d
                          ? "bg-[#2DD4A7] text-[#04150F]"
                          : "text-[#8B97AE] hover:text-[#E8ECF4]"
                      }`}
                    >{d === 30 ? t("card.wager.period30") : t("card.wager.period90")}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("onboarding.reportModeLabel")}</div>
                <div className="inline-flex border border-white/20" role="tablist">
                  {[
                    { v: "categorized", label: t("onboarding.reportMode.categorized") },
                    { v: "total_only",  label: t("onboarding.reportMode.totalOnly") },
                  ].map((m) => (
                    <button
                      key={m.v}
                      type="button"
                      data-testid={`ob-case-report-mode-${m.v}`}
                      disabled={!!f.offeredCase}
                      onClick={() => set("caseReportMode", m.v)}
                      className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        f.caseReportMode === m.v
                          ? "bg-[#2DD4A7] text-[#04150F]"
                          : "text-[#8B97AE] hover:text-[#E8ECF4]"
                      }`}
                    >{m.label}</button>
                  ))}
                </div>
              </div>
            </div>
            {/* Iter 217 — Opt-out control lives in Section 3 (see above).
                Section 4 is fully hidden when opt-out is on, so this block
                only renders in "will play" mode. */}
            {f.caseAccounts.map((acct, i) => (
              <div
                key={acct._uid}
                data-testid={`ob-case-acct-${i}`}
                className={`space-y-3 ${i > 0 ? "pt-4 border-t border-white/10" : ""}`}
              >
                {f.caseAccounts.length > 1 && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                    {(acct.gamingAccountName && acct.gamingAccountName.trim()) || t("onboarding.accountNumber").replace("{n}", String(i + 1))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                      {t("onboarding.case.wagerLabel")}
                    </Label>
                    <div className="mt-1 flex items-center">
                      <span className="text-[#8B97AE] font-mono text-sm px-2">$</span>
                      <Input
                        data-testid={i === 0 ? "ob-case-wager" : `ob-case-wager-${i}`}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        disabled={!!f.offeredCase || !!f.noCaseSite}
                        value={f.noCaseSite ? "" : fmt(Number(acct.wager || 0))}
                        onChange={(e) => setCaseAcctWager(acct._uid, e.target.value)}
                        className="rounded-none bg-transparent border-white/20 h-9 font-mono tabular-nums text-right disabled:opacity-40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {f.offeredCase && (
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-cat-locked-case">{t("onboarding.offered.categoriesLocked")}</div>
            )}
            <div className="text-[10px] font-mono uppercase tracking-widest" data-testid="ob-case-sum">
              <span className="text-[#8B97AE]">{t("onboarding.case.totalLabel")}: </span>
              <span className="tabular-nums text-[#E8ECF4]" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>${fmt(caseWager)}</span>
            </div>
            {/* Weighted case card (iter 33). Single-field mode: the weighted
                figure equals the entered wager (coef 1.0). Kept as a
                confirming/decorative display — reference-only, NEVER
                persisted into any billing/rebate field. */}
            <div className="border border-[#2DD4A7]/30 bg-[#2DD4A7]/5 p-3" data-testid="ob-case-weighted">
              <div className="flex items-center gap-2">
                {/* Iter 181 — mirrors the casino weighted-wager card: larger,
                    higher-contrast info button so it clearly reads as tappable. */}
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">{t("onboarding.case.weightedLabel")}</div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="ob-case-weighted-info-btn"
                      aria-label={t("onboarding.case.weightedHowLabel")}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-[#2DD4A7] bg-[#2DD4A7] text-[#04150F] hover:bg-[#2DD4A7]/85 hover:text-[#04150F] focus:outline-none focus:ring-2 focus:ring-[#2DD4A7]/70 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    data-testid="ob-case-weighted-info-popover"
                    className="w-80 rounded-none border-[#2DD4A7]/40 bg-[#0B0E14] text-[#E8ECF4] p-4 shadow-xl"
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#2DD4A7]">{t("onboarding.case.weightedHowLabel")}</div>
                      {caseCfg?.version ? (
                        <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B97AE]">v{caseCfg.version}</div>
                      ) : null}
                    </div>
                    <p className="mt-2 text-xs text-[#E8ECF4] leading-relaxed">{t("onboarding.case.weightedFormula")}</p>
                    <p className="mt-3 text-xs text-[#8B97AE] leading-relaxed" data-testid="ob-case-weighted-tooltip-disclaimer">
                      {t("onboarding.weightedTooltipBrief")}
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Iter 181 — inline "SELF-REPORTED ESTIMATE · … · V2" caption
                  removed; disclaimer surfaces via the info popover instead. */}
              <div className="mt-1">
                <span
                  className="font-display text-3xl font-black tracking-tighter tabular-nums text-[#E8ECF4]"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                  data-testid="ob-case-weighted-value"
                >${fmt(Math.round(weightedCase))}</span>
              </div>
            </div>
            {/* Iter 69 — offered volume (case). Same gating + bounds rule
                as casino. Only rendered when the case block itself is
                visible; disabled until the case wager is entered. */}
            <div className="border border-[#F5B841]/30 bg-[#F5B841]/[0.04] p-3" data-testid="ob-offered-case-card">
              <div className="flex items-center gap-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" htmlFor="ob-offered-case">{t("onboarding.offered.label")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="ob-offered-case-info-btn"
                      aria-label={t("onboarding.offered.infoTitle")}
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#F5B841]/60 text-[#F5B841] hover:bg-[#F5B841]/10 focus:outline-none focus:ring-2 focus:ring-[#F5B841]/60 transition-colors"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    data-testid="ob-offered-case-info-popover"
                    className="w-80 rounded-none border-[#F5B841]/40 bg-[#0B0E14] text-[#E8ECF4] p-4 shadow-xl"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5B841] mb-2">{t("onboarding.offered.infoTitle")}</div>
                    <p className="text-xs text-[#E8ECF4] leading-relaxed">{t("onboarding.offered.infoBody")}</p>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Iter 181 — parallel to the casino offered-volume caption. */}
              <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-offered-case-not-binding">
                {t("onboarding.offeredNotBinding")}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[#8B97AE] font-mono text-sm px-1">$</span>
                <Input
                  id="ob-offered-case"
                  data-testid="ob-offered-case"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={caseOfferedGated}
                  value={f.offeredCase ? fmt(offeredCaseNum) : ""}
                  onChange={(e) => setOffered("offeredCase", e.target.value)}
                  placeholder={caseOfferedGated ? "" : t("onboarding.offered.placeholder").replace("{min}", fmt(caseBounds.min)).replace("{max}", fmt(caseBounds.max))}
                  className="rounded-none bg-transparent border-white/20 h-10 font-mono tabular-nums text-right disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                />
              </div>
              {/* Iter 181 — gated hint copy removed; input stays visually
                  disabled via `disabled` prop until a case wager is entered. */}
              {!caseOfferedGated && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-offered-case-range">
                  {t("onboarding.offered.range").replace("{min}", fmt(caseBounds.min)).replace("{max}", fmt(caseBounds.max))}
                </div>
              )}
              {/* Iter 199 — Case-side 90-day projection helper. Shows only
                  when the case reporting period is 30, mirroring the casino
                  "projected90d" caption. */}
              {/* Iter 221 — Case projected-90d caption restored (same
                  rationale as Casino above). */}
              {Number(f.caseReportPeriodDays) === 30 && !caseOfferedGated && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-offered-case-projected90d">
                  {t("onboarding.offered.projected90d")}
                </div>
              )}
              {offeredCaseError && (
                <div className="mt-2 text-[11px] font-mono text-[#F26D6D]" data-testid="ob-offered-case-error">{offeredCaseError}</div>
              )}
            </div>
          </section>
        )}

        <section className="border border-white/8 p-6 space-y-4">
          <h2 className="font-display font-bold text-lg">{stepNums.proofs} · {t("onboarding.proofs")}</h2>
          <p className="text-xs text-[#8B97AE]">{t("onboarding.proofsHint")}</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-proofs-limits">{t("onboarding.proofsLimits")}</p>
          {/* Iter 181 — replace the native file-input chrome (which shows
              browser/OS-locale strings like "Datei auswählen / Keine Datei
              ausgewählt" regardless of our EN/DE toggle) with a hidden native
              input + custom label. Wiring stays identical: same `upload()`
              handler, same PROOF_ACCEPT, same reset-after-select behaviour. */}
          <div className="flex flex-wrap items-center gap-3" data-testid="ob-proofs-control">
            <label
              htmlFor="ob-proofs-input"
              data-testid="ob-proofs-choose-btn"
              className="inline-flex items-center border border-white/20 bg-transparent text-[#E8ECF4] px-4 py-2 font-mono text-xs uppercase tracking-widest cursor-pointer hover:bg-white/10 transition-colors focus-within:ring-2 focus-within:ring-[#2DD4A7]/60"
            >{t("onboarding.proofs.chooseBtn")}</label>
            <span className="text-xs font-mono text-[#8B97AE]" data-testid="ob-proofs-status">
              {(() => {
                const inFlight = Object.keys(uploads).length;
                const done = proofs.length;
                const n = inFlight + done;
                return n === 0 ? t("onboarding.proofs.noneSelected") : t("onboarding.proofs.selectedCount").replace("{n}", String(n));
              })()}
            </span>
            <input
              id="ob-proofs-input"
              data-testid="ob-proofs-input"
              type="file"
              multiple
              accept={PROOF_ACCEPT}
              onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }}
              className="sr-only"
            />
          </div>
          {Object.entries(uploads).length > 0 && (
            <ul className="space-y-1" data-testid="ob-uploads">
              {Object.entries(uploads).map(([id, u]) => (
                <li key={id} className="text-xs font-mono border border-white/10 px-3 py-2" data-testid={`ob-upload-${id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{u.name}</span>
                    <span className={u.err ? "text-[#F26D6D]" : "text-[#2DD4A7]"}>{u.err ? "failed" : `${u.pct}%`}</span>
                  </div>
                  <div className="mt-1 h-1 bg-white/10">
                    <div className={`h-1 ${u.err ? "bg-[#F26D6D]" : "bg-[#2DD4A7]"}`} style={{ width: `${u.pct}%` }} />
                  </div>
                  {u.err && <div className="mt-1 text-[#F26D6D]">{u.err}</div>}
                </li>
              ))}
            </ul>
          )}
          <ul className="space-y-2" data-testid="ob-proofs-list">
            {proofs.length === 0 && Object.keys(uploads).length === 0 && <li className="text-xs text-[#F26D6D] flex items-center gap-1"><AlertCircle className="w-3 h-3" />{t("onboarding.proofsRequired")}</li>}
            {proofs.map((p) => (
              <li key={p.path} className="flex items-center justify-between border border-white/10 px-3 py-2 text-xs font-mono">
                <span className="truncate">{p.filename || p.path}</span>
                <button type="button" data-testid={`ob-proof-remove-${p.path.slice(-12)}`} onClick={() => remove(p.path)} className="text-[#F26D6D] hover:text-[#E8ECF4]"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </section>

        <section className="border border-white/8 p-6 space-y-4">
          <h2 className="font-display font-bold text-lg">{stepNums.preferences} · {t("onboarding.preferences")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ob-prefs">
            {PREFS.map((p) => (
              <button key={p} type="button" data-testid={`pref-${p}`} onClick={() => togglePref(p)} className={`px-3 h-9 border font-mono text-[11px] uppercase tracking-widest transition-colors ${f.streamerPreferences.includes(p) ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-white/15 text-[#E8ECF4] hover:bg-white/5"}`}>{t(`prefs.${p}`)}</button>
            ))}
          </div>
          <Input data-testid="ob-prefNote" placeholder={t("onboarding.prefNote")} value={f.streamerPrefNote} onChange={(e) => set("streamerPrefNote", e.target.value)} className="rounded-none bg-transparent border-white/20 h-11" />
          <div className="pt-4 border-t border-white/5">
            <Label className="text-xs uppercase tracking-widest text-[#8B97AE]" htmlFor="ob-aboutMe">{t("onboarding.aboutMe")}</Label>
            {/* Iter 181 — orange PII-warning paragraph removed from view; the
                200-char cap and server-side PII stripper remain enforced. */}
            <textarea
              id="ob-aboutMe"
              data-testid="ob-aboutMe"
              value={f.aboutMe}
              onChange={(e) => set("aboutMe", e.target.value.slice(0, 200))}
              placeholder={t("onboarding.aboutMePlaceholder")}
              maxLength={200}
              rows={3}
              className="w-full rounded-none bg-transparent border border-white/20 focus:border-[#2DD4A7] focus:outline-none text-sm text-[#E8ECF4] p-3 resize-none"
            />
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mt-1 text-right" data-testid="ob-aboutMe-count">
              <span className={`tabular-nums ${f.aboutMe.length > 200 ? "text-[#F26D6D]" : ""}`}>{f.aboutMe.length}</span> / 200 {t("onboarding.charCount")}
            </div>
          </div>
        </section>

        {/* Iter 76 — Block 6 is now TWO independent sub-sections. Casino and
            Case Site each have their own "no preference" flag, their own
            row list, add/remove controls and 5-cap. Cross-blocking (the
            previous single-array bug) is impossible by construction. */}
        <section className="border border-white/8 p-6 space-y-6" data-testid="onb-preferred-sites">
          <div>
            <h2 className="font-display font-bold text-lg">{stepNums.preferredSites} · {t("onboarding.preferredSites.title")}</h2>
            <p className="text-xs text-[#8B97AE] mt-2">{t("onboarding.preferredSites.body")}</p>
          </div>

          {/* ---------- Casino sub-section ---------- */}
          <div className="space-y-3" data-testid="onb-preferred-casinos">
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-sm font-bold text-[#E8ECF4]">{t("onboarding.preferredSites.verticalCasino")}</h3>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-preferred-casinos-counter">
                {f.noPreferredCasino ? 0 : f.preferredCasinos.length} / {MAX_PREFERRED_SITES}
              </span>
            </div>

            <label className="flex items-start gap-3 border border-white/10 p-3">
              <Checkbox
                data-testid="ob-no-pref-casino"
                checked={f.noPreferredCasino}
                onCheckedChange={(v) => setF((old) => ({ ...old, noPreferredCasino: !!v }))}
                className="mt-0.5 rounded-none border-white/40 data-[state=checked]:bg-[#F5B841] data-[state=checked]:text-[#04150F]"
              />
              <div>
                <div className="text-sm text-[#E8ECF4]">{t("onboarding.preferredSites.noCasinoLabel")}</div>
                <div className="text-[10px] font-mono text-[#8B97AE] mt-0.5">{t("onboarding.preferredSites.noCasinoHint")}</div>
              </div>
            </label>

            {!f.noPreferredCasino && (
              <>
                <ul className="space-y-3">
                  {f.preferredCasinos.map((s, i) => {
                    const hint = casinoHints[i] || {};
                    return (
                      <li key={s._uid} className="border border-white/10 p-4 space-y-3" data-testid={`ob-pref-casino-${i}`}>
                        <div>
                          <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                            {t("onboarding.preferredSites.siteCasino")}
                          </Label>
                          <Select
                            value={s.siteId || (s.otherName ? "__other" : "")}
                            onValueChange={(v) => casinoList.update(s._uid, { siteId: v === "__other" ? "" : v, otherName: v === "__other" ? s.otherName : "" })}
                          >
                            <SelectTrigger data-testid={`ob-pref-casino-select-${i}`} className="rounded-none bg-transparent border-white/20 h-10 mt-1"><SelectValue placeholder={t("onboarding.preferredSites.pickSite")} /></SelectTrigger>
                            <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
                              {casinos.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                              <SelectItem value="__other">{t("onboarding.other")}</SelectItem>
                            </SelectContent>
                          </Select>
                          {(!s.siteId) && (
                            <Input
                              data-testid={`ob-pref-casino-other-${i}`}
                              className="mt-2 rounded-none bg-transparent border-white/20 h-10"
                              placeholder={t("onboarding.preferredSites.otherNameCasino")}
                              value={s.otherName}
                              onChange={(e) => casinoList.update(s._uid, { otherName: e.target.value })}
                            />
                          )}
                        </div>
                        <div className="min-h-[16px] text-[11px] font-mono">
                          {hint.duplicate && <div className="text-[#F26D6D]" data-testid={`ob-pref-casino-hint-duplicate-${i}`}>{t("onboarding.preferredSites.duplicate")}</div>}
                          {hint.matchesCurrent && <div className="text-[#F26D6D]" data-testid={`ob-pref-casino-hint-current-${i}`}>{t("onboarding.preferredSites.matchesCurrent")}</div>}
                        </div>
                        <div className="flex justify-end">
                          {f.preferredCasinos.length > 1 && (
                            <button
                              type="button"
                              data-testid={`ob-pref-casino-remove-${i}`}
                              onClick={() => casinoList.remove(s._uid)}
                              className="text-[11px] font-mono uppercase tracking-widest text-[#F26D6D] hover:text-[#E8ECF4] transition-colors flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />{t("onboarding.preferredSites.remove")}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    data-testid="ob-pref-casino-add"
                    onClick={casinoList.add}
                    disabled={f.preferredCasinos.length >= MAX_PREFERRED_SITES}
                    className="inline-flex items-center gap-2 px-3 h-9 border border-[#2DD4A7]/50 text-[#2DD4A7] font-mono text-[11px] uppercase tracking-widest hover:bg-[#2DD4A7]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3 h-3" />{t("onboarding.preferredSites.add")}
                  </button>
                  {f.preferredCasinos.length >= MAX_PREFERRED_SITES && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-pref-casino-max-notice">{t("onboarding.preferredSites.max")}</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ---------- Case Site sub-section (only when case vertical is live) ---------- */}
          {caseVisible && (
            <div className="space-y-3 pt-4 border-t border-white/8" data-testid="onb-preferred-cases">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-sm font-bold text-[#E8ECF4]">{t("onboarding.preferredSites.verticalCase")}</h3>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="ob-preferred-cases-counter">
                  {f.noPreferredCaseSite ? 0 : f.preferredCaseSites.length} / {MAX_PREFERRED_SITES}
                </span>
              </div>

              <label className="flex items-start gap-3 border border-white/10 p-3">
                <Checkbox
                  data-testid="ob-no-pref-case"
                  checked={f.noPreferredCaseSite}
                  onCheckedChange={(v) => setF((old) => ({ ...old, noPreferredCaseSite: !!v }))}
                  className="mt-0.5 rounded-none border-white/40 data-[state=checked]:bg-[#F5B841] data-[state=checked]:text-[#04150F]"
                />
                <div>
                  <div className="text-sm text-[#E8ECF4]">{t("onboarding.preferredSites.noCaseLabel")}</div>
                  <div className="text-[10px] font-mono text-[#8B97AE] mt-0.5">{t("onboarding.preferredSites.noCaseHint")}</div>
                </div>
              </label>

              {!f.noPreferredCaseSite && (
                <>
                  <ul className="space-y-3">
                    {f.preferredCaseSites.map((s, i) => {
                      const hint = caseHints[i] || {};
                      return (
                        <li key={s._uid} className="border border-white/10 p-4 space-y-3" data-testid={`ob-pref-case-${i}`}>
                          <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                              {t("onboarding.preferredSites.siteCase")}
                            </Label>
                            <Select
                              value={s.siteId || (s.otherName ? "__other" : "")}
                              onValueChange={(v) => caseList.update(s._uid, { siteId: v === "__other" ? "" : v, otherName: v === "__other" ? s.otherName : "" })}
                            >
                              <SelectTrigger data-testid={`ob-pref-case-select-${i}`} className="rounded-none bg-transparent border-white/20 h-10 mt-1"><SelectValue placeholder={t("onboarding.preferredSites.pickSite")} /></SelectTrigger>
                              <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
                                {caseOpsCase.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("onboarding.case.groupCase")}</SelectLabel>
                                    {caseOpsCase.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                                {caseOpsPack.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("onboarding.case.groupPack")}</SelectLabel>
                                    {caseOpsPack.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                                <SelectItem value="__other">{t("onboarding.other")}</SelectItem>
                              </SelectContent>
                            </Select>
                            {(!s.siteId) && (
                              <Input
                                data-testid={`ob-pref-case-other-${i}`}
                                className="mt-2 rounded-none bg-transparent border-white/20 h-10"
                                placeholder={t("onboarding.preferredSites.otherNameCase")}
                                value={s.otherName}
                                onChange={(e) => caseList.update(s._uid, { otherName: e.target.value })}
                              />
                            )}
                          </div>
                          <div className="min-h-[16px] text-[11px] font-mono">
                            {hint.duplicate && <div className="text-[#F26D6D]" data-testid={`ob-pref-case-hint-duplicate-${i}`}>{t("onboarding.preferredSites.duplicate")}</div>}
                            {hint.matchesCurrent && <div className="text-[#F26D6D]" data-testid={`ob-pref-case-hint-current-${i}`}>{t("onboarding.preferredSites.matchesCurrent")}</div>}
                          </div>
                          <div className="flex justify-end">
                            {f.preferredCaseSites.length > 1 && (
                              <button
                                type="button"
                                data-testid={`ob-pref-case-remove-${i}`}
                                onClick={() => caseList.remove(s._uid)}
                                className="text-[11px] font-mono uppercase tracking-widest text-[#F26D6D] hover:text-[#E8ECF4] transition-colors flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" />{t("onboarding.preferredSites.remove")}
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      data-testid="ob-pref-case-add"
                      onClick={caseList.add}
                      disabled={f.preferredCaseSites.length >= MAX_PREFERRED_SITES}
                      className="inline-flex items-center gap-2 px-3 h-9 border border-[#2DD4A7]/50 text-[#2DD4A7] font-mono text-[11px] uppercase tracking-widest hover:bg-[#2DD4A7]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3 h-3" />{t("onboarding.preferredSites.add")}
                    </button>
                    {f.preferredCaseSites.length >= MAX_PREFERRED_SITES && (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="ob-pref-case-max-notice">{t("onboarding.preferredSites.max")}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        <section className="border border-white/8 p-6 space-y-4">
          <h2 className="font-display font-bold text-lg">{stepNums.wallet} · {t("onboarding.wallet")}</h2>
          <p className="text-xs text-[#F5B841] font-mono" data-testid="ob-wallet-rebate-help">{t("onboarding.wallet.rebateHelp")}</p>
          <p className="text-xs text-[#F5B841]">{t("auth.walletHelp")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("onboarding.coin")}</Label>
              <Select value={f.walletCoin} onValueChange={setCoin}>
                <SelectTrigger data-testid="ob-coin" className="rounded-none bg-transparent border-white/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">{COIN_OPTIONS.map((c) => <SelectItem key={c} value={c} data-testid={`ob-coin-opt-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("onboarding.network")}</Label>
              {COIN_NETWORKS[f.walletCoin].length === 1 ? (
                <Input readOnly data-testid="ob-network-readonly" value={COIN_NETWORKS[f.walletCoin][0]} className="rounded-none bg-white/[0.03] border-white/20 h-11 mt-2 font-mono text-sm cursor-not-allowed" />
              ) : (
                <Select value={f.walletNetwork} onValueChange={setNetwork}>
                  <SelectTrigger data-testid="ob-network" className="rounded-none bg-transparent border-white/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">{COIN_NETWORKS[f.walletCoin].map((n) => <SelectItem key={n} value={n} data-testid={`ob-network-opt-${n}`}>{n}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div>
            <Input data-testid="ob-address" placeholder={t("onboarding.address")} value={f.walletAddress} onChange={(e) => set("walletAddress", e.target.value)} className="rounded-none bg-transparent border-white/20 h-11 font-mono text-sm" />
            {walletAddrError && (
              <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[#F26D6D]" data-testid="ob-address-error">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{walletAddrError}</span>
              </div>
            )}
          </div>
        </section>

        <Button
          data-testid="ob-submit"
          type="submit"
          disabled={busy || proofs.length === 0 || !preferredSitesValid || !offeredValid || !walletValid}
          className="w-full rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-12 font-semibold transition-colors"
        >{busy ? "…" : t("onboarding.submit")}</Button>
      </form>
    </div>
  );
}
